import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { GridRow } from 'dibs-elements/exports/Grid.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';

import HpSharedPromoModulePromoItem from './HpSharedPromoModulePromoItem.tsx';
import { HpSharedModuleContainer } from '../HpSharedModuleContainer/HpSharedModuleContainer.tsx';
import { trackPromoImpressions, trackPromoClick } from './helpers/promoTracking.ts';
import { trackModuleLocation, trackModule } from '../helpers/moduleLocationTracking.ts';

import {
    type HpSharedPromoModule_componentModule$key,
    type HpSharedPromoModule_componentModule$data,
} from './__generated__/HpSharedPromoModule_componentModule.graphql.ts';

type PromoItem = NonNullable<
    NonNullable<HpSharedPromoModule_componentModule$data['messages']>['items']
>[0];

type Props = {
    componentModule: HpSharedPromoModule_componentModule$key;
    startingPromoIndex?: number;
    useLazyLoadImages: boolean;
    isMobile: boolean;
    moduleIndex: number;
    totalModules: number;
};

const HpSharedPromoModule: FC<Props> = ({
    componentModule: componentModuleRef,
    startingPromoIndex = 0,
    useLazyLoadImages,
    isMobile,
    moduleIndex,
    totalModules,
}) => {
    const componentModule = useFragment(
        graphql`
            fragment HpSharedPromoModule_componentModule on PromoModule {
                cmsDisplayTitle
                messages(userType: $userType) {
                    title
                    ctaUrl
                    ctaCopy
                    items {
                        cmsId
                        ... on PromoItem @skip(if: $isMobile) {
                            imageWidth
                        }
                        ...promoTracking_promotion
                        ...HpSharedPromoModulePromoItem_promotion
                    }
                    ...promoTracking_messages
                }
            }
        `,
        componentModuleRef
    );

    if (!componentModule) {
        return null;
    }

    const { messages, cmsDisplayTitle } = componentModule;
    const { title, ctaUrl, ctaCopy } = messages || {};
    const items = (messages?.items || []).filter(filterNulls);
    const isSinglePromo = items.length === 1;

    const getShownImageWidth = (item: PromoItem): number => parseInt(item?.imageWidth || '', 10);

    const totalImagesWidth = !isMobile
        ? items.reduce((acc, item) => acc + getShownImageWidth(item), 0)
        : undefined;

    const getWidthPercentage = (item: PromoItem): number => {
        if (!isMobile && !isSinglePromo) {
            const imageWidth = getShownImageWidth(item);

            if (imageWidth && totalImagesWidth) {
                return imageWidth / totalImagesWidth;
            }
        }

        return 1;
    };

    const handleInitialDisplay = ({ inViewport }: { inViewport: boolean }): void => {
        if (inViewport) {
            trackPromoImpressions({
                messages,
                startingIndex: startingPromoIndex,
            });
        }
    };

    const splitTitle = title?.split(' ');
    const firstWord = splitTitle?.[0];
    const restOfTitle = splitTitle?.slice(1).join(' ');

    const ctaButton =
        ctaUrl && ctaCopy ? (
            <Button href={ctaUrl} size="medium" type="transparent" fullWidth={isMobile}>
                {ctaCopy}
            </Button>
        ) : null;

    return (
        <HpSharedModuleContainer addTopGap={!title}>
            <div
                className={classnames(
                    dibsCss.mtLarge,
                    dibsCss.mbMedium,
                    dibsCss.flex,
                    dibsCss.justifyBetween,
                    dibsCss.itemsEnd
                )}
            >
                {title && (
                    <div
                        className={classnames({
                            [dibsCss.sassyFontHeaderXxLargeItalic]: !isMobile,
                            [dibsCss.sassyFontHeaderLargeItalic]: isMobile,
                        })}
                    >
                        {firstWord}
                        {restOfTitle && (
                            <span
                                className={classnames({
                                    [dibsCss.sassyFontHeaderXxLarge]: !isMobile,
                                    [dibsCss.sassyFontHeaderLarge]: isMobile,
                                })}
                            >
                                {' '}
                                {restOfTitle}
                            </span>
                        )}
                    </div>
                )}
                {!isMobile && <>{ctaButton}</>}
            </div>
            <VisibilityTracker
                onVisibilityChange={({ isVisible }) =>
                    handleInitialDisplay({ inViewport: isVisible })
                }
            />
            <GridRow>
                {items.map((promotion, index) => (
                    <HpSharedPromoModulePromoItem
                        key={promotion?.cmsId || index}
                        promotion={promotion}
                        useLazyLoadImages={useLazyLoadImages}
                        onClick={() => {
                            const promoIndex = index + startingPromoIndex;
                            trackPromoClick({
                                promotion,
                                index: promoIndex,
                            });
                            trackModuleLocation({
                                label: `promo banner #${promoIndex}`,
                                moduleIndex,
                                totalModules,
                            });
                            trackModule(cmsDisplayTitle);
                        }}
                        index={index}
                        isSinglePromo={isSinglePromo}
                        isMobile={isMobile}
                        widthPercentage={getWidthPercentage(promotion)}
                    />
                ))}
            </GridRow>

            {isMobile && ctaButton}
        </HpSharedModuleContainer>
    );
};

export default HpSharedPromoModule;
